import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { ErrorState } from '@/components/ui/states';

interface Props<T> {
  query: UseQueryResult<T>;
  /** Shown on first load. Pass a shaped skeleton, not a spinner. */
  loading: ReactNode;
  /** Rendered when the request succeeded but returned nothing to show. */
  empty?: { when: (data: T) => boolean; render: ReactNode };
  children: (data: T) => ReactNode;
}

/**
 * The loading / error / empty decision, written once.
 *
 * Every list and detail screen needs the same four branches, and hand-rolling
 * them per screen is how one page ends up retrying on a 403 while another shows
 * a spinner forever. Screens pass a skeleton and a render function; this owns
 * the order those branches are checked in.
 */
export function QueryBoundary<T>({ query, loading, empty, children }: Props<T>) {
  // Error first: a failed refetch must not keep showing stale rows as if fine.
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  // isPending, not isLoading — with placeholderData we want the previous page
  // to stay visible rather than flashing a skeleton on every page change.
  if (query.isPending) return <>{loading}</>;

  const data = query.data as T;
  if (empty?.when(data)) return <>{empty.render}</>;

  return <>{children(data)}</>;
}
